import { compact, uniq } from 'lodash';
import { translateToPrql } from '@gosupersimple/penguino';
import { common, model } from '@gosupersimple/types';

import { notNil, unlessNil } from '@/lib/utils';
import { exhaustiveCheck } from '@/lib/exhaustive';

import { createDereferencedPipeline } from '@/core/pipeline';
import { extractCombineSteps } from '@/core/operation';

import {
  AggregationType,
  Fields,
  DereferencedPipelineOperation,
  Model,
  PipelineState,
  QueryVariables,
  CompositeFilterCondition,
  TimeAggregationPeriod,
  DereferencedPipeline,
  Pipeline,
  Exploration,
  Aggregation,
  Metric,
  Field,
  Relation,
  PipelineStateRelation,
  KeyedAggregation,
} from '../types';
import {
  isExprAggregation,
  isKeyedAggregation,
  isMetricAggregation,
  isValueExpression,
} from './operation';
import {
  FieldOrGroup,
  dereferencePipeline,
  ensureUniqueFieldNames,
  flattenFieldGroups,
} from './utils';
import { getModelOrThrow, getModel, getJoinKeyOnBase } from '../model/utils';
import { convertFieldsForPenguinoContext } from '../utils/penguino';
import { getVariableExpression } from '../utils';
import { convertPropertyValueType } from '../input';
import { getConditionField } from '../edit-pipeline/utils/filter';

export interface PipelineStateContext {
  models: Model[];
  variables: QueryVariables;
  metrics: Metric[];
}

export class InvalidOperationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidOperationError';
  }
}

export const InvalidModel = {
  modelId: 'invalid',
  name: 'Invalid model',
  primaryKey: [],
  properties: [],
  relations: [],
  labels: {},
};

const constructFieldRelation = (model: Model, key: string) =>
  model.primaryKey.includes(key)
    ? {
        modelId: model.modelId,
        name: model.name,
        key,
      }
    : unlessNil(
        model.relations.find(
          (relation) =>
            (relation.type === 'hasOne' &&
              relation.joinStrategy &&
              'joinKeyOnBase' in relation.joinStrategy &&
              key === relation.joinStrategy?.joinKeyOnBase) ??
            false,
        ),
        (relation) => {
          return relation.joinStrategy && 'joinKeyOnRelated' in relation.joinStrategy
            ? {
                modelId: relation.modelId,
                name: relation.name,
                key: relation.joinStrategy.joinKeyOnRelated,
              }
            : undefined;
        },
      );

const constructField = (
  key: string,
  name: string,
  type: model.PropertyType | null,
  precision?: TimeAggregationPeriod,
  format?: common.PropertyValueFormat,
  model?: Model,
  keyInRelatedModel?: string,
  description?: string,
) => {
  return {
    key,
    name,
    type,
    pk: model?.primaryKey.includes(key) ?? false,
    model:
      model === undefined
        ? undefined
        : { modelId: model.modelId, name: model.name, propertyKey: keyInRelatedModel ?? key },
    relation:
      model === undefined ? undefined : constructFieldRelation(model, keyInRelatedModel ?? key),
    precision,
    format,
    description,
  };
};

export const getFieldsWithRelatedFields = (
  state: PipelineState,
  models: Model[],
): FieldOrGroup[] => {
  const relatedFields = state.relations
    .filter((relation) => relation.type === 'hasOne' || relation.type === 'hasOneThrough')
    .map((relation) => {
      const relatedModel = getModel(models, relation.modelId);
      if (!relatedModel) {
        return undefined;
      }

      return {
        key: `${relation.baseModelId}.${relation.key}`,
        name: relation.name,
        fields: relatedModel.properties.map((field) => ({
          ...field,
          model: { modelId: relatedModel.modelId, name: relatedModel.name, propertyKey: field.key },
          relation,
        })),
      };
    })
    .filter(notNil);

  return [...state.fields, ...relatedFields];
};

/**
 * Find the (possibly renamed) field that is the given property of the given model.
 */
export const findModelPropertyField = (fields: Field[], modelId: string, propertyKey: string) =>
  fields.find(
    (field) => field.model?.modelId === modelId && field.model?.propertyKey === propertyKey,
  );

export const getNextPipelineStateOrThrow = (
  state: PipelineState,
  operation: DereferencedPipelineOperation,
  operationIndex: number,
  ctx: PipelineStateContext,
): Omit<PipelineState, 'relations'> => {
  const { models, variables, metrics } = ctx;
  if (operation.disabled === true) {
    return state;
  }
  switch (operation.operation) {
    case 'filter': {
      validateFilterCondition(
        operation.parameters,
        getFieldsWithRelatedFields(state, models),
        state.fields,
        variables,
      );
      return state;
    }
    case 'deriveField': {
      const { returnType, format, precision } = getExpressionResult(
        operation.parameters.value.expression,
        state.fields,
        variables,
      );

      const fields = [
        ...state.fields,
        {
          key: operation.parameters.key,
          name: operation.parameters.fieldName,
          // Ignore Enum types from expressions for now. Since we do not have the lineage, anything downstream
          // does not know how to fetch enum options.
          type: convertPropertyValueType(returnType === 'Enum' ? 'String' : returnType) ?? 'String',
          format,
          precision,
        },
      ];

      return { ...state, fields };
    }

    case 'relationAggregate': {
      const relatedPipeline =
        'relation' in operation.parameters
          ? operation.parameters.pipeline === undefined
            ? {
                baseModelId: findValidRelation(
                  state.relations,
                  models,
                  operation.parameters.relation.key,
                  operation.parameters.relation.modelId,
                ).modelId,
                operations: [],
              }
            : operation.parameters.pipeline
          : operation.parameters.pipeline;

      const relatedPipelineState = getFinalState(relatedPipeline, ctx);
      const relatedPipelineFields = relatedPipelineState.fields;
      const extendedRelatedPipelineFields = getFieldsWithRelatedFields(
        relatedPipelineState,
        models,
      );

      const joinStrategy =
        'joinStrategy' in operation.parameters ? operation.parameters.joinStrategy : undefined;
      if (joinStrategy !== undefined) {
        // Validate that the join keys exist
        const { joinKeyOnBase, joinKeyOnRelated } = joinStrategy;
        const baseField = state.fields.find((field) => field.key === joinKeyOnBase);
        if (baseField === undefined) {
          throw new InvalidOperationError(`Invalid base column ${joinKeyOnBase}`);
        }
        const relatedField = relatedPipelineFields.find((field) => field.key === joinKeyOnRelated);
        if (relatedField === undefined) {
          throw new InvalidOperationError(`Invalid related column ${joinKeyOnRelated}`);
        }
      }

      const relationKey =
        'relation' in operation.parameters ? operation.parameters.relation.key : undefined;

      validateSlice(operation.parameters.slice, relatedPipelineFields);
      if (operation.parameters.filter !== undefined) {
        validateFilterCondition(
          operation.parameters.filter,
          extendedRelatedPipelineFields,
          [
            { fields: state.fields },
            { key: state.model.modelId, fields: state.fields }, // TODO: figure out 'current table' namespace
            { key: relationKey ?? relatedPipeline.baseModelId, fields: relatedPipelineFields },
          ],
          variables,
        );
      }

      const fields = state.fields.concat(
        operation.parameters.aggregations.map((aggregation) => {
          const relatedField = relatedPipelineFields.find(
            (field) => isKeyedAggregation(aggregation) && field.key === aggregation.key,
          );
          if (isKeyedAggregation(aggregation) && relatedField === undefined) {
            throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
          }
          return constructField(
            aggregation.property.key,
            aggregation.property.name,
            getResultTypeForAggregation(aggregation, relatedPipelineFields, variables, metrics),
            undefined,
            getResultFormatForAggregation(aggregation, relatedPipelineFields, ctx),
            aggregation.type === 'first'
              ? getModel(models, relatedField?.model?.modelId ?? relatedPipeline.baseModelId)
              : undefined,
            aggregation.type === 'first'
              ? (relatedField?.model?.propertyKey ?? aggregation.key)
              : undefined,
          );
        }),
      );

      return { ...state, fields };
    }

    case 'addRelatedColumn': {
      const { relatedModel, relation } = getRelatedModelOrThrow(
        state,
        models,
        operation.parameters.relation.key,
        operation.parameters.relation.modelId,
      );

      if (!['hasOne', 'hasOneThrough'].includes(relation.type)) {
        throw new InvalidOperationError(`Cannot add column from ${relation.name}`);
      }

      const relatedPipelineFields = getFinalState(
        createDereferencedPipeline({
          baseModelId: operation.parameters.pipeline?.baseModelId ?? relatedModel.modelId,
          operations: operation.parameters.pipeline?.operations ?? [],
        }),
        ctx,
      ).fields;

      const newFields = operation.parameters.columns.map((column) => {
        const relatedField = relatedPipelineFields.find((field) => field.key === column.key);
        if (relatedField === undefined) {
          throw new InvalidOperationError(`Field "${column.key}" not found`);
        }

        return constructField(
          column.property.key,
          column.property.name,
          relatedField.type,
          relatedField?.precision,
          relatedField?.format,
          getModel(models, relatedField?.model?.modelId ?? relatedModel.modelId),
          relatedField?.model?.propertyKey ?? column.key,
          relatedField?.description,
        );
      });

      const fields = state.fields.concat(newFields);

      return {
        ...state,
        fields,
      };
    }

    case 'groupAggregate': {
      const { groups, aggregations, slice } = operation.parameters;

      validateSlice(slice, state.fields);
      validateGroupDependencies(groups, state.fields);

      if (aggregations.length === 0) {
        // Only apply new precision values from groupings
        return {
          ...state,
          fields: state.fields.map((field) => ({
            ...field,
            precision:
              precisionToTimeInterval(
                groups.find(({ key }) => key === field.key)?.precision,
                variables,
              ) ?? field.precision,
          })),
        };
      }

      const fields = [
        ...groups.map(({ key, precision }) => {
          const field = state.fields.find((f) => f.key === key);
          if (field === undefined) {
            throw new InvalidOperationError(`Field "${key}" not found`);
          }

          return constructField(
            key,
            field.name,
            precision === 'day_of_week' ? 'Integer' : field.type,
            precisionToTimeInterval(precision, variables),
            precision === 'day_of_week' ? 'date' : field.format,
            getModel(models, field.model?.modelId ?? ''),
            field.model?.propertyKey,
            field.description,
          );
        }),
        ...aggregations.map((aggregation) => {
          const field = isKeyedAggregation(aggregation)
            ? state.fields.find((f) => f.key === aggregation.key)
            : undefined;
          if (isKeyedAggregation(aggregation) && field === undefined) {
            throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
          }

          return constructField(
            aggregation.property.key,
            aggregation.property.name,
            getResultTypeForAggregation(aggregation, state.fields, variables, metrics),
            undefined,
            getResultFormatForAggregation(aggregation, state.fields, ctx),
            aggregation.type === 'first'
              ? getModel(models, field?.model?.modelId ?? '')
              : undefined,
            aggregation.type === 'first'
              ? (field?.model?.propertyKey ?? aggregation.key)
              : undefined,
          );
        }),
      ];

      return {
        ...state,
        fields,
      };
    }

    case 'switchToRelation': {
      const { relatedModel } = getRelatedModelOrThrow(
        state,
        models,
        operation.parameters.relation.key,
        operation.parameters.relation.modelId,
      );
      return {
        model: relatedModel,
        fields: relatedModel.properties.map(({ key, name, type, format, description }) =>
          constructField(key, name, type, undefined, format, relatedModel, undefined, description),
        ),
      };
    }

    case 'joinPipeline': {
      return extractCombineSteps(operation).reduce((memo, step, i, items) => {
        const joinStrategy = step.joinStrategy;
        if (joinStrategy === undefined) {
          throw new InvalidOperationError(`Join strategy not found for step ${i}`);
        }

        const baseModelId = step.pipeline.baseModelId;
        const rightModel = getModel(models, baseModelId);
        if (rightModel === undefined) {
          throw new InvalidOperationError(`Model "${baseModelId}" not found`);
        }
        const rightModelName = rightModel.name;
        const pipelineState = getFinalState(
          createDereferencedPipeline({
            baseModelId,
            operations: step.pipeline.operations,
          }),
          ctx,
        );
        const leftFieldKeys = memo.fields.map((f) => f.key);
        const rightSideFields = pipelineState.fields.filter(
          (field) =>
            !(
              joinStrategy.joinKeyOnBase === joinStrategy.joinKeyOnRelated &&
              field.key === joinStrategy.joinKeyOnRelated
            ),
        );
        if (!leftFieldKeys.includes(joinStrategy.joinKeyOnBase)) {
          throw new InvalidOperationError(`Field "${joinStrategy.joinKeyOnBase}" not found`);
        }
        if (!pipelineState.fields.some((field) => field.key === joinStrategy.joinKeyOnRelated)) {
          throw new InvalidOperationError(`Field "${joinStrategy.joinKeyOnRelated}" not found`);
        }
        // This must mimick backend's field key & name renaming convention exactly
        const rightFieldMap = new Map(
          rightSideFields.map((f) => {
            if (!leftFieldKeys.includes(f.key)) {
              return [f.key, f.key];
            }

            // Use stepId if available
            if ('stepId' in step && step.stepId !== undefined) {
              return [f.key, `${f.key}_${step.stepId}`];
            }

            // Old format:
            return [f.key, `join_${operationIndex}${items.length > 1 ? `_${i}` : ''}_r_${f.key}`];
          }),
        );

        const fields = memo.fields.concat(
          ensureUniqueFieldNames(
            state.fields,
            rightSideFields.map((field) => ({
              ...field,
              key: rightFieldMap.get(field.key) ?? field.key,
            })),
            rightModelName,
          ),
        );

        return { ...memo, fields };
      }, state);
    }

    case 'sql': {
      const sqlModel: Model = {
        modelId: 'sql-model',
        name: 'Ad-Hoc model from SQL',
        primaryKey: [],
        properties: (operation.parameters.fields ?? []).map(({ key, type }) => ({
          key,
          name: key,
          type: type ?? 'String',
        })),
        relations: [],
        labels: {},
      };

      return { ...state, model: sqlModel, fields: sqlModel.properties };
    }

    default: {
      return state;
    }
  }
};

export const getNextPipelineState = (
  state: PipelineState,
  operation: DereferencedPipelineOperation,
  operationIndex: number,
  ctx: PipelineStateContext,
): Omit<PipelineState, 'relations'> => {
  try {
    return getNextPipelineStateOrThrow(state, operation, operationIndex, ctx);
  } catch (e) {
    if (!(e instanceof InvalidOperationError)) {
      throw e;
    }
    return state;
  }
};

const getFieldTypeByAggregationType = (type: AggregationType) => {
  switch (type) {
    case 'count':
      return 'Integer';
    default:
      return null;
  }
};

export const getPipelineStateAtIndexOrThrow = (
  pipeline: DereferencedPipeline,
  index: number,
  ctx: PipelineStateContext,
): PipelineState => {
  const state = pipeline.operations.slice(0, index).reduce(
    (state, operation, i) => {
      const nextState = getNextPipelineStateOrThrow(state, operation, i, ctx);
      return { ...nextState, relations: getAvailableRelations(nextState.fields, ctx.models) };
    },
    getBaseStateOrThrow(pipeline.baseModelId, ctx.models),
  );

  return state;
};

export const getPipelineStateAtIndex = (
  pipeline: DereferencedPipeline,
  index: number,
  ctx: PipelineStateContext,
): PipelineState => {
  return pipeline.operations.slice(0, index).reduce(
    (state, operation, i) => {
      const nextState = getNextPipelineState(state, operation, i, ctx);
      return { ...nextState, relations: getAvailableRelations(nextState.fields, ctx.models) };
    },
    getBaseState(pipeline.baseModelId, ctx.models),
  );
};

export const getBaseStateOrThrow = (baseModelId: string, models: Model[]): PipelineState => {
  const baseModel = getModelOrThrow(models, baseModelId);
  const fields = baseModel.properties.map(({ key, name, type, format, description }) =>
    constructField(key, name, type, undefined, format, baseModel, undefined, description),
  );
  return {
    model: baseModel,
    relations: getAvailableRelations(fields, models),
    fields,
  };
};

export const getBaseState = (baseModelId: string, models: Model[]): PipelineState => {
  const baseModel = getModel(models, baseModelId);

  if (baseModel === undefined) {
    return {
      model: InvalidModel,
      relations: [],
      fields: [],
    };
  }

  const fields = baseModel.properties.map(({ key, name, type, format, description }) =>
    constructField(key, name, type, undefined, format, baseModel, undefined, description),
  );
  return {
    model: baseModel,
    relations: getAvailableRelations(fields, models),
    fields,
  };
};

export const getFinalStateOrThrow = (
  pipeline: DereferencedPipeline,
  ctx: PipelineStateContext,
): PipelineState => getPipelineStateAtIndexOrThrow(pipeline, pipeline.operations.length, ctx);

export const getFinalState = (
  pipeline: DereferencedPipeline,
  ctx: PipelineStateContext,
): PipelineState => getPipelineStateAtIndex(pipeline, pipeline.operations.length, ctx);

export const getPipelineFields = (
  pipeline: Pipeline,
  exploration: Exploration,
  ctx: PipelineStateContext,
) => getDereferencedPipelineFields(dereferencePipeline(pipeline, exploration), ctx);

export const getDereferencedPipelineFields = (
  pipeline: DereferencedPipeline,
  ctx: PipelineStateContext,
) => getFinalStateOrThrow(pipeline, ctx).fields;

/**
 * Returns all the fields in all the pipeline states.
 */
export const getAllPipelineFields = (
  dereferencedPipeline: DereferencedPipeline,
  ctx: PipelineStateContext,
) => {
  return [
    ...dereferencedPipeline.operations.reduce<Field[]>((acc, _, i) => {
      const { fields } = getPipelineStateAtIndex(dereferencedPipeline, i, ctx);
      return uniq([...acc, ...fields]);
    }, []),
    ...getFinalState(dereferencedPipeline, ctx).fields,
  ];
};

const getAvailableRelations = (fields: Field[], models: Model[]) =>
  uniq(compact(fields.map((field) => field.model?.modelId))).flatMap((modelId) => {
    const model = getModel(models, modelId);
    if (model === undefined) {
      throw new InvalidOperationError(
        `Model "${modelId}" not found in ${models.map((m) => m.modelId).join(', ')}`,
      );
    }
    return (
      model.relations
        ?.filter((relation) => joinKeyOnBaseExists(fields, relation, model))
        .map((relation) => ({ ...relation, baseModelId: model.modelId })) ?? []
    );
  });

export const findValidRelation = (
  relations: PipelineStateRelation[],
  models: Model[],
  relationKey: string,
  baseModelId?: string,
) => {
  const matchingRelations = relations.filter(
    (r) => r.key === relationKey && (r.baseModelId === baseModelId || baseModelId === undefined),
  );
  if (matchingRelations.length === 0) {
    throw new InvalidOperationError(
      `Relation "${relationKey}" not found${baseModelId !== undefined ? ` in model ${baseModelId}` : ''}`,
    );
  }
  if (matchingRelations.length > 1) {
    throw new InvalidOperationError(
      `Relation key "${relationKey}" is ambiguous. Please provide a model ID.`,
    );
  }
  const relatedModel = getModel(models, matchingRelations[0].modelId);
  if (relatedModel === undefined) {
    throw new InvalidOperationError(`Model "${matchingRelations[0].modelId}" not found`);
  }
  return matchingRelations[0];
};

const joinKeyOnBaseExists = (fields: Field[], relation: Relation, model: Model) => {
  const joinKey = getJoinKeyOnBase(relation, model);
  return findModelPropertyField(fields, model.modelId, joinKey) !== undefined;
};

const getRelatedModelOrThrow = (
  state: PipelineState,
  models: Model[],
  relationKey: string,
  modelId?: string,
) => {
  const relation = findValidRelation(state.relations, models, relationKey, modelId);
  return { relatedModel: getModelOrThrow(models, relation.modelId), relation };
};

const validateFilterCondition = (
  condition: CompositeFilterCondition,
  fields: FieldOrGroup[],
  fieldsForExpression: FieldOrGroup[],
  variables: QueryVariables,
): boolean => {
  if (condition.operator === 'and' || condition.operator === 'or') {
    return condition.operands.every((c) =>
      validateFilterCondition(c, fields, fieldsForExpression, variables),
    );
  } else if (!('key' in condition)) {
    return true;
  }

  const field = getConditionField(condition, fields);

  if (field === undefined) {
    throw new InvalidOperationError(`Field "${condition.key}" not found`);
  }

  if (isValueExpression(condition.value)) {
    const { returnType } = getExpressionResult(
      condition.value.expression,
      fieldsForExpression,
      variables,
    );
    const expectedType =
      condition.operator === 'in' || condition.operator === 'notin' ? 'Array' : null;
    const typesMatch = expectedType === null || returnType === expectedType;
    if (!typesMatch) {
      throw new InvalidOperationError(`Expected type ${expectedType}, got ${returnType}`);
    }
  }

  return true;
};

const validateSlice = (slice: common.Slice | undefined, fields: Fields) => {
  if (slice === undefined) {
    return true;
  }
  if (slice.limit !== undefined && slice.limit < 0) {
    throw new InvalidOperationError('Slice limit must be a positive number');
  }
  if (slice.offset !== undefined && slice.offset < 0) {
    throw new InvalidOperationError('Slice offset must be a positive number');
  }
  validateSorting(slice?.sort, fields);
};

const validateSorting = (sorting: common.Sorting[] | undefined, fields: Fields) => {
  if (sorting === undefined) {
    return true;
  }
  sorting.forEach((sort) => {
    if (!fields.some((field) => field.key === sort.key)) {
      throw new InvalidOperationError(`Field "${sort.key}" not found`);
    }
  });
};

const validateGroupDependencies = (grouping: common.Grouping[], fields: Fields) => {
  grouping.forEach(({ key }) => {
    if (!fields.some((field) => field.key === key)) {
      throw new InvalidOperationError(`Field "${key}" not found`);
    }
  });
};

const getExpressionResult = (
  expression: string,
  fields: FieldOrGroup[],
  variables: QueryVariables,
) => {
  try {
    const { returnType, format, precision } = translateToPrql(expression, {
      fields: convertFieldsForPenguinoContext(fields),
      variables,
    });
    return { returnType: convertPropertyValueType(returnType), format, precision };
  } catch (e) {
    if (e instanceof Error) {
      throw new InvalidOperationError(e.message);
    }
    throw new InvalidOperationError(`Invalid expression "${expression}"`);
  }
};

const getResultTypeForAggregation = (
  aggregation: Aggregation,
  fields: FieldOrGroup[],
  variables: QueryVariables,
  metrics: Metric[],
) => {
  return (
    getFieldTypeByAggregationType(aggregation.type) ??
    (isKeyedAggregation(aggregation)
      ? getResultTypeforKeyedAggregation(aggregation, fields)
      : isExprAggregation(aggregation)
        ? (convertPropertyValueType(
            getExpressionResult(aggregation.value.expression, fields, variables).returnType,
          ) ?? ('String' as const))
        : isMetricAggregation(aggregation)
          ? getResultTypeForMetric(aggregation.metricId, metrics)
          : ('String' as const))
  );
};

const getResultFormatForAggregation = (
  aggregation: Aggregation,
  fields: Fields,
  ctx: PipelineStateContext,
) => {
  switch (aggregation.type) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'first':
    case 'last':
    case 'median': {
      const field = fields.find((field) => field.key === aggregation.key);
      if (field === undefined) {
        throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
      }
      return field.format;
    }
    case 'metric': {
      const metric = ctx.metrics.find((m) => m.metricId === aggregation.metricId);
      if (metric === undefined) {
        throw new InvalidOperationError(`Metric "${aggregation.metricId}" not found`);
      }
      const state = getFinalState(
        createDereferencedPipeline({
          baseModelId: metric.definition.modelId,
          operations: metric.definition.operations ?? [],
        }),
        ctx,
      );
      return getResultFormatForAggregation(
        { ...metric.definition.aggregation, property: aggregation.property },
        state.fields,
        ctx,
      );
    }
    default:
      return undefined;
  }
};

const getResultTypeforKeyedAggregation = (
  aggregation: KeyedAggregation,
  fields: FieldOrGroup[],
) => {
  const type = aggregation.type;
  switch (type) {
    case 'count_distinct':
      return 'Integer' as const;
    case 'sum':
    case 'min':
    case 'max':
    case 'first':
    case 'last':
      return (
        flattenFieldGroups(fields).find((field) => field.key === aggregation.key)?.type ?? null
      );
    case 'avg':
    case 'median':
      return 'Float';
    default:
      exhaustiveCheck(type);
  }
};

const getResultTypeForMetric = (metricId: string, metrics: Metric[]) => {
  if (!metrics.some((m) => m.metricId === metricId)) {
    throw new InvalidOperationError(`Metric "${metricId}" not found`);
  }
  return 'Number' as const;
};

export const precisionToTimeInterval = (
  precision: common.TimeInterval | common.ExpressionValue | undefined,
  variables: common.QueryVariables,
) => {
  if (precision === undefined) {
    return undefined;
  }

  const timeIntervalValue = isValueExpression(precision)
    ? variables.find(({ key }) => getVariableExpression(key) === precision.expression)?.value
    : precision;
  const validation = common.timeInterval.safeParse(timeIntervalValue);
  if (!validation.success) {
    throw new InvalidOperationError(`Invalid precision value: ${timeIntervalValue}`);
  }
  return validation.data;
};
